import type { Actuacion, SnapshotProcess, Sujeto } from '@legalhermes/shared/domain';
import { getYear } from '../../utils/dates';

export type ProcessStatusKey = 'active' | 'old' | 'noActivity';

export const PROCESS_STATUS: Record<
  ProcessStatusKey,
  { text: string; pillClass: string; dotClass: string }
> = {
  active: {
    text: 'Movimiento reciente',
    pillClass: 'text-success border-success/30 bg-success-subtle',
    dotClass: 'bg-success',
  },
  old: {
    text: 'Sin movimientos recientes',
    pillClass: 'text-accent border-accent/30 bg-accent-subtle',
    dotClass: 'bg-accent',
  },
  noActivity: {
    text: 'Sin actuaciones',
    pillClass: 'text-ink-muted border-ink-muted/25 bg-ink-muted/10',
    dotClass: 'bg-ink-muted',
  },
};

export function getProcessStatusKey(process: SnapshotProcess): ProcessStatusKey {
  if (!process.fechaUltimaActuacion || process.fechaUltimaActuacion === '') return 'noActivity';
  const daysDiff = Math.floor(
    (Date.now() - new Date(process.fechaUltimaActuacion).getTime()) / 86400000
  );
  return daysDiff <= 30 ? 'active' : 'old';
}

export function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return 'hace un momento';
  if (diffMinutes < 60) return `hace ${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''}`;
  if (diffHours < 24) return `hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
  if (diffDays < 30) return `hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
  return 'hace más de un mes';
}

export function dedupeProcesses(processes: SnapshotProcess[]): SnapshotProcess[] {
  return Array.from(new Map(processes.map((p) => [p.idProceso, p] as const)).values());
}

/** Orden DESC por una fecha ISO, con valores nulos/vacíos siempre al final. */
export function sortByDateDesc<T>(items: T[], getDate: (item: T) => string | null): T[] {
  return [...items].sort((a, b) => {
    const da = getDate(a);
    const db = getDate(b);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return new Date(db).getTime() - new Date(da).getTime();
  });
}

/** Orden por última actuación DESC (decisión deliberada, difiere de fechaRadicacion en HistoricalData). */
export function sortByLastActuacionDesc(processes: SnapshotProcess[]): SnapshotProcess[] {
  return sortByDateDesc(processes, (p) => p.fechaUltimaActuacion);
}

export function sortActuacionesDesc<T extends { fechaActuacion: string | null }>(items: T[]): T[] {
  return sortByDateDesc(items, (item) => item.fechaActuacion);
}

const DEMANDANTE_PATTERNS = ['demandante', 'accionante', 'ejecutante', 'convocante', 'querellante'];
const DEMANDADO_PATTERNS = ['demandado', 'accionado', 'ejecutado', 'convocado', 'querellado'];

export type PartySide = 'demandante' | 'demandado' | 'otro';

export interface Party {
  side: PartySide;
  tipo: string;
  nombre: string;
  inicial: string;
}

const SIDE_ROLE_LABEL: Record<PartySide, string> = {
  demandante: 'Quien demanda',
  demandado: 'Quien es demandado',
  otro: 'Participa en el proceso',
};

const SIDE_EXPLANATION: Record<PartySide, string> = {
  demandante: 'Inició el proceso',
  demandado: 'Persona o entidad contra quien va el proceso',
  otro: 'Interviene en el proceso',
};

function normalizeTipo(value: string | null): string {
  return (value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u{300}-\u{36f}]/gu, '')
    .trim();
}

function sideOf(tipo: string | null): PartySide {
  const normalized = normalizeTipo(tipo);
  if (DEMANDANTE_PATTERNS.some((p) => normalized.includes(p))) return 'demandante';
  if (DEMANDADO_PATTERNS.some((p) => normalized.includes(p))) return 'demandado';
  return 'otro';
}

export function roleLabel(party: Party): string {
  return party.side === 'otro' ? party.tipo : SIDE_ROLE_LABEL[party.side];
}

export function roleExplanation(side: PartySide): string {
  return SIDE_EXPLANATION[side];
}

/** Sujetos con nombre utilizable, clasificados por lado y con inicial para el avatar. */
export function toParties(sujetos: Sujeto[]): Party[] {
  return (sujetos || [])
    .filter((s) => s.nombre?.trim())
    .map((s) => {
      const nombre = (s.nombre as string).trim();
      return {
        side: sideOf(s.tipo),
        tipo: s.tipo?.trim() || 'Sujeto procesal',
        nombre,
        inicial: nombre.charAt(0).toUpperCase(),
      };
    });
}

export interface CaseTitle {
  demandante: string | null;
  demandado: string | null;
  fallback: boolean;
}

function joinSide(names: string[]): string | null {
  const [first] = names;
  if (!first) return null;
  return names.length > 1 ? `${first} y otros` : first;
}

/**
 * Título del caso en cascada: ambos lados → "A contra B"; varios por lado →
 * "A y otros"; un solo lado conocido → ese lado; ninguno → despacho.
 */
export function buildCaseTitle(parties: Party[], despacho: string | null): CaseTitle {
  const demandante = joinSide(parties.filter((p) => p.side === 'demandante').map((p) => p.nombre));
  const demandado = joinSide(parties.filter((p) => p.side === 'demandado').map((p) => p.nombre));

  if (demandante && demandado) return { demandante, demandado, fallback: false };

  const single = demandante ?? demandado;
  if (single) return { demandante: single, demandado: null, fallback: false };

  return {
    demandante: despacho?.trim() || 'Proceso sin despacho registrado',
    demandado: null,
    fallback: true,
  };
}

export interface ActuacionYearGroup {
  year: number | null;
  items: Actuacion[];
}

/** Agrupa actuaciones ya ordenadas por año, conservando el orden recibido. */
export function groupActuacionesByYear(actuaciones: Actuacion[]): ActuacionYearGroup[] {
  const groups: ActuacionYearGroup[] = [];
  for (const item of actuaciones) {
    const year = getYear(item.fechaActuacion);
    const last = groups.at(-1);
    if (last && last.year === year) {
      last.items.push(item);
    } else {
      groups.push({ year, items: [item] });
    }
  }
  return groups;
}

/**
 * Extrae Demandante/Demandado de los sujetos del proceso; fallback a los dos
 * primeros sujetos. Comparte el clasificador de lados con `toParties` para que
 * ambas vistas coincidan en quién es demandante (incluye ejecutante, accionante…).
 */
export function getPartesFromSujetos(sujetos: SnapshotProcess['sujetos']): {
  demandante: string | null;
  demandado: string | null;
} {
  if (!sujetos || sujetos.length === 0) return { demandante: null, demandado: null };
  const findBySide = (side: PartySide) =>
    sujetos.find((s) => sideOf(s.tipo) === side)?.nombre ?? null;

  let demandante = findBySide('demandante');
  let demandado = findBySide('demandado');

  if (!demandante && !demandado) {
    demandante = sujetos[0]?.nombre ?? null;
    demandado = sujetos[1]?.nombre ?? null;
  }
  return { demandante, demandado };
}
